import type { SkinKind } from "@highmarch/domain";
import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { priceHtml } from "./econArt";
import { confirmPurchase, econCue, tickPill } from "./purchaseFx";
import { refusalText } from "./shopActions";
import { skinsLink } from "./skinsLink";
import { cobbles, pennant } from "./skinsSheet";
import { type SkinCard, actionFor, statusOf } from "./skinsModel";
import { framePreview, killPreview } from "./trophyPreviews";
import { commanderTitle } from "./commanderSheet";
import { openTrophyHall } from "./trophySkins";
import { petLink } from "../pets/petLink";
import { petPortraitHtml } from "../pets/petArt";
import { SkinPreview, type PreviewTarget } from "../../render/skins/skinPreview";
import { SKIN_LOOKS } from "../../render/skins/looks";
import { HubScene } from "../../render/hubScene";
import { SHOP_SKIN } from "../shopSkin";
import { uiUrl } from "../portraits";
import { notEnoughGems } from "./getGold";

/** One skin as the Shop lists it, and who it goes on (a tower type, a pet; null for the rest). */
export interface ShopSkin { readonly id: string; readonly kind: SkinKind; readonly who: string | null }

/** What the card says a skin dresses. */
export function skinFits(kind: SkinKind, anyTower: boolean, who: string | null): string {
  switch (kind) {
    case "tower": return anyTower ? "Any tower" : `${skinsLink()?.towers().find((t) => t.id === who)?.name ?? "Tower"} only`;
    case "fortress": return "Fortress";
    case "troop": return "Troops";
    case "pet": return `${petLink.name(who ?? "")} coat`;
    case "emblem": return "Banner";
    case "road": return "Road";
    case "frame": return "Title frame";
    case "kill": return "Kill effect";
  }
}

/** The card a skin shows on (its access now, for `who`). */
export function shopSkinCard(s: ShopSkin): SkinCard | null {
  const link = skinsLink();
  const look = SKIN_LOOKS[s.id];
  if (!link || !look) return null;
  const k = link.cosmetics, now = link.now();
  const access = k.access(s.id, s.who, now);
  const worn = k.worn(s.kind, s.who) === s.id;
  const towerName = s.kind === "pet" ? petLink.name(s.who ?? "") : link.towers().find((t) => t.id === s.who)?.name ?? "Tower";
  const price = access.have ? null : k.gemPrice(s.id, s.who);
  const gems = price === null ? null : { price, affordable: k.wallet.gems >= price };
  return { id: s.id, look, worn, access, status: statusOf(access, worn, s.kind, towerName), gems };
}

/**
 * Tap a skin in the Shop's Skins tab and see it (owner 2026-10-20: "it should show a preview of the skin, including
 * the animation"): a light card over the Shop with the real model wearing it, turning and playing its own motion (a
 * tower kit's moving parts, a fortress skin's reveal and its flying companions, a troop's blows), the pennant, the
 * road, the frame or the kill effect for the rest; its name and what it dresses; and the one thing to do with it: BUY,
 * WEAR, WORN, or how it is earned. A skin for any tower picks the tower it is shown (and worn) on.
 */
export function openSkinPreview(parent: HTMLElement, s0: ShopSkin, changed: () => void): HTMLElement | null {
  const link = skinsLink();
  if (!link) return null;
  const k = link.cosmetics;
  const data = k.skin(s0.id);
  if (!data || !SKIN_LOOKS[s0.id]) return null;
  const towers = link.towers();
  const anyTower = s0.kind === "tower" && data.tower === undefined;
  let who = s0.who;
  let preview: SkinPreview | null = null;

  const scrim = document.createElement("div");
  scrim.className = `scrim skprev ${SHOP_SKIN}`;
  const close = () => { preview?.dispose(); preview = null; scrim.remove(); };
  scrim.addEventListener("click", (e) => { if (e.target === scrim) close(); });

  const target = (): PreviewTarget | null => {
    if (s0.kind === "fortress") return { kind: "fortress", tier: HubScene.tierFor(link.baseLevel()) };
    if (s0.kind === "troop") return { kind: "troop" };
    if (s0.kind === "pet") return who && SkinPreview.canShowPet(who, s0.id) ? { kind: "pet", petId: who } : null;
    if (s0.kind === "tower") return { kind: "tower", archId: who ?? "archer", tier: HubScene.tierFor(towers.find((t) => t.id === who)?.level ?? 1) };
    return null;
  };

  const render = (): void => {
    const card = shopSkinCard({ ...s0, who });
    if (!card) { close(); return; }
    const a = actionFor(card, s0.kind, towers.find((t) => t.id === who)?.name ?? "Tower");
    const buyBtn = (cur: "gems" | "marks", price: number, ok: boolean) => `<button type="button" class="btn k-price dbuy skp-go${ok ? "" : " short"}" data-cur="${cur}"><span class="disp">BUY</span>${priceHtml(cur, fmtCompact(price), 18)}</button>`;
    const act = a.kind === "buy" ? `${buyBtn(a.currency, a.price, a.affordable)}${a.alsoGems ? buyBtn("gems", a.alsoGems.price, a.alsoGems.affordable) : ""}${a.alt ? (a.alt.go ? `<button type="button" class="skp-alt" data-go="${a.alt.go}">${a.alt.text}</button>` : `<div class="skp-alt">Or free: ${a.alt.text}</div>`) : ""}`
      : a.kind === "wear" ? `<button type="button" class="btn gold skp-go"><span class="disp">WEAR</span></button>`
      : a.kind === "worn" ? `<div class="skp-note on">Worn</div>`
      : a.kind === "market" ? `<button type="button" class="btn k-price dbuy skp-go"><span class="disp">BLACK MARKET</span>${priceHtml("gems", fmtCompact(a.price), 18)}</button>`
      : a.kind === "trophyHall" ? `<button type="button" class="btn gold skp-go"><span class="disp">TROPHY HALL</span></button>`
      : `<div class="skp-note">${a.text}</div>`;
    const chips = anyTower && towers.length > 1
      ? `<div class="skp-towers">${towers.map((t) => `<button type="button" class="skp-t${t.id === who ? " on" : ""}" data-t="${t.id}">${t.name}</button>`).join("")}</div>` : "";
    scrim.innerHTML = `<div class="skp-card" role="dialog" aria-label="${card.look.name}">
      <button type="button" class="skp-x" aria-label="Close">✕</button>
      <div class="skp-stage"></div>
      <div class="skp-copy"><b class="disp">${card.look.name}</b><small>${skinFits(s0.kind, anyTower, who)}</small></div>${chips}${act}</div>`;
    scrim.querySelector<HTMLButtonElement>(".skp-x")!.onclick = close;
    for (const b of scrim.querySelectorAll<HTMLButtonElement>(".skp-t")) b.onclick = () => { if (b.dataset.t !== who) { who = b.dataset.t!; render(); } };
    for (const b of scrim.querySelectorAll<HTMLButtonElement>(".skp-go")) b.addEventListener("click", () => void go(card, a.kind, b.dataset.cur as "gems" | "marks" | undefined));
    scrim.querySelector<HTMLButtonElement>("button.skp-alt")?.addEventListener("click", (e) => void go(card, (e.currentTarget as HTMLElement).dataset.go!));
    // the stage: the turning model (kept across re-renders, re-dressed), or the kind's picture
    const stage = scrim.querySelector<HTMLElement>(".skp-stage")!;
    const t = target();
    if (t) {
      const w = 600, h = Math.round(w * 0.78);
      preview ??= new SkinPreview(w / 2, h / 2, true);
      stage.classList.add("lawn");
      stage.style.backgroundImage = `url("${uiUrl("skins/stage_meadow.webp")}")`;
      stage.append(preview.canvas);
      void preview.show(t, s0.id);
    } else {
      preview?.dispose(); preview = null;
      stage.classList.add("pic");
      if (s0.kind === "emblem") stage.append(pennant(card));
      else if (s0.kind === "road") stage.append(cobbles(card));
      else if (s0.kind === "frame") stage.append(framePreview(card.look, commanderTitle()));
      else if (s0.kind === "kill") stage.append(killPreview(card.look));
      else if (s0.kind === "pet") stage.insertAdjacentHTML("beforeend", `<div class="sk-pet">${petPortraitHtml(who ?? "", 160, s0.id)}</div>`);
    }
  };

  const go = async (card: SkinCard, kind: string, cur?: "gems" | "marks"): Promise<void> => {
    if (kind === "wear") {
      const r = k.equip(s0.kind, s0.id, who);
      if (r !== true) { toast(parent, refusalText(r)); return; }
      econCue("tap"); link.changed(); changed();
      toast(parent, `${card.look.name} worn`);
      render();
      return;
    }
    if (kind === "market") { close(); link.openBlackMarket(); return; }
    if (kind === "trophyHall") { if (openTrophyHall(parent, s0.id)) close(); return; }
    if (kind !== "buy" || card.access.have) return;
    // the skin's own Shop price (gems or Marks), or its gem price beside its free route (owner 2026-10-21)
    const own = "price" in card.access && (card.access.by === "gems" || card.access.by === "marks") ? { by: card.access.by, price: card.access.price } : null;
    const a = cur === "gems" && card.gems ? { by: "gems" as const, price: card.gems.price } : own ?? (card.gems ? { by: "gems" as const, price: card.gems.price } : null);
    if (!a) return;
    const wallet = k.wallet, have = a.by === "gems" ? wallet.gems : wallet.marks;
    if (have < a.price) {
      if (a.by === "gems") void notEnoughGems(parent, a.price - have);
      else toast(parent, `You need ${fmtCompact(a.price - have)} more Marks`);
      return;
    }
    const ok = await confirmPurchase(parent, { title: "SKIN", currency: a.by, price: a.price, body: `<p><b>${card.look.name}</b> · ${skinFits(s0.kind, anyTower, who)}</p>` });
    if (!ok) return;
    const r = k.buy(s0.id, link.now(), "hub", a.by);
    if (r !== true) { toast(parent, refusalText(r)); return; }
    econCue("buy");
    k.equip(s0.kind, s0.id, who);
    link.changed(); changed();
    tickPill(a.by, have, a.by === "gems" ? wallet.gems : wallet.marks);
    toast(parent, `${card.look.name} is yours, and worn`);
    render();
  };

  render();
  parent.append(scrim);
  return scrim;
}
